import { compact } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  OrganizationTalentIndicatorFragment,
  TrendReportGroupBy,
  TrendReportType,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import Sidebar from "@components/sidebar/sidebar";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import TrendReportFilterContainer from "./filters/trend-report-filter-container";
import {
  TrendReportDataType,
  TrendReportViewOptions,
} from "./trend-report-view-options-sidebar-constants";

const TrendReportViewOptionsSidebar = ({
  talentIndicators,
  viewOptions,
  onClose,
  onUpdateViewOptions,
}: {
  talentIndicators: OrganizationTalentIndicatorFragment[];
  viewOptions: TrendReportViewOptions;
  onClose: () => void;
  onUpdateViewOptions: (opts: TrendReportViewOptions) => void;
}) => {
  const label = useLabel();
  const [proposedViewOptions, setProposedViewOptions] =
    useState<TrendReportViewOptions>(viewOptions);

  const talentIndicatorOptions = useMemo(
    () =>
      talentIndicators.map((indicator) => ({
        value: indicator.id,
        label: `Talent Indicator: ${
          indicator.customName && indicator.customName.trim() !== ""
            ? indicator.customName
            : indicator.name
        }`,
      })),
    [talentIndicators],
  );
  const scoreTypeOptions = useMemo(
    () => [
      {
        value: TrendReportType.Distribution,
        label: "Distribution (Bar Graph)",
      },
      {
        value: TrendReportType.Average,
        label: "Average (Line Graph)",
      },
    ],
    [],
  );
  const groupByOptions = useMemo(
    () => [
      {
        value: TrendReportGroupBy.Organization,
        label: "All Responders",
      },
      {
        value: TrendReportGroupBy.Team,
        label: `${label("team", { capitalize: true })}`,
      },
      {
        value: TrendReportGroupBy.Manager,
        label: "Manager",
      },
    ],
    [label],
  );

  return (
    <Sidebar
      title="View Options"
      onClose={() => {
        onUpdateViewOptions(proposedViewOptions);
        onClose();
      }}
    >
      <div className="px-6 pb-6">
        <FormFields
          width={120}
          fields={[
            {
              title: "View Name",
              render: () => (
                <Input
                  placeholder="Enter a name..."
                  maxLength={512}
                  value={proposedViewOptions.name}
                  onChange={(e) =>
                    setProposedViewOptions((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              ),
            },
          ]}
        />
      </div>

      <div className="border-t p-6">
        <div className="p-4 border rounded-md">
          <div className="flex items-center justify-between">
            <div className="font-medium">
              {label("review", { titleCase: true })} Start Date
            </div>
          </div>
          <div className="mt-2 flex gap-2 items-center">
            <InputDatePicker
              showIcon
              isClearable
              wrapperClassName="flex-1"
              placeholderText="From"
              date={
                proposedViewOptions.startDate
                  ? moment(proposedViewOptions.startDate).toDate()
                  : null
              }
              onChangeDate={(date) =>
                setProposedViewOptions((prev) => ({
                  ...prev,
                  startDate:
                    date !== null
                      ? moment(date).format("YYYY-MM-DD")
                      : undefined,
                }))
              }
              dateFormat="MMM d, yyyy"
              className="px-4 py-2 block w-full sm:text-sm border rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <TopicflowIcon
              type="rightArrow"
              className="text-gray-500 w-4 h-4"
            />
            <InputDatePicker
              showIcon
              isClearable
              wrapperClassName="flex-1"
              placeholderText="To"
              date={
                proposedViewOptions.endDate
                  ? moment(proposedViewOptions.endDate).toDate()
                  : null
              }
              onChangeDate={(date) =>
                setProposedViewOptions((prev) => ({
                  ...prev,
                  endDate:
                    date !== null
                      ? moment(date).format("YYYY-MM-DD")
                      : undefined,
                }))
              }
              dateFormat="MMM d, yyyy"
              className="px-4 py-2 block w-full sm:text-sm border rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="border-t p-6">
        <div className="font-semibold mb-4">Parameters</div>
        <FormFields
          width={120}
          fields={compact([
            {
              title: "Data type",
              render: () => (
                <ComboboxGeneric
                  options={talentIndicatorOptions}
                  onChangeValue={(opt) =>
                    setProposedViewOptions((prev) => ({
                      ...prev,
                      dataType: {
                        type: TrendReportDataType.TALENT_INDICATOR,
                        talentIndicatorId: opt.value,
                      },
                    }))
                  }
                  value={
                    talentIndicatorOptions.find(
                      ({ value }) =>
                        value ===
                        proposedViewOptions.dataType?.talentIndicatorId,
                    ) ?? null
                  }
                />
              ),
            },
            {
              title: "Score type",
              render: () => (
                <ComboboxGeneric
                  options={scoreTypeOptions}
                  onChangeValue={(opt) =>
                    setProposedViewOptions((prev) => ({
                      ...prev,
                      reportType: opt.value,
                    }))
                  }
                  value={
                    scoreTypeOptions.find(
                      ({ value }) => value === proposedViewOptions.reportType,
                    ) ?? null
                  }
                />
              ),
            },
            proposedViewOptions.reportType === TrendReportType.Average && {
              title: "Group by",
              render: () => (
                <ComboboxGeneric
                  options={groupByOptions}
                  onChangeValue={(opt) =>
                    setProposedViewOptions((prev) => ({
                      ...prev,
                      groupBy: opt.value,
                      filters: [],
                    }))
                  }
                  value={
                    groupByOptions.find(
                      ({ value }) => value === proposedViewOptions.groupBy,
                    ) ?? null
                  }
                />
              ),
            },
          ])}
        />
      </div>

      <div className="border-t p-6">
        <div className="font-semibold mb-4">Filters</div>
        <div>
          <TrendReportFilterContainer
            trendReportGroupBy={proposedViewOptions.groupBy}
            trendReportType={proposedViewOptions.reportType}
            filters={proposedViewOptions.filters}
            onUpdateFilters={(newFilters) =>
              setProposedViewOptions({
                ...proposedViewOptions,
                filters: newFilters,
              })
            }
          />
        </div>
      </div>
    </Sidebar>
  );
};

export default TrendReportViewOptionsSidebar;
